import type { Group } from '@cheqroom/api';
import type { usePermissions } from '@cheqroom/hooks';
import { ComponentType, createContext, FC, PropsWithChildren, ReactNode, useContext } from 'react';

import type { FieldIconResolver, FieldValueFormatter } from './fields/Fields.types';

type PermissionsProps = Parameters<typeof usePermissions>[0];

/**
 * The minimal translate-function contract the Info components need — deliberately not `i18next`'s own
 * `TFunction`. Two workspaces can have distinct physical i18next installs (same version, separate
 * copies from nohoisting) that TypeScript treats as nominally incompatible for `TFunction`'s
 * deeply-generic signature; a real `t` from either one is structurally assignable to this much
 * simpler shape without a cast, so that friction never crosses this package's public API.
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One i18n namespace per Info type, in `@cheqroom/i18n` `locales/<lang>/<namespace>.json`. */
export type InfoNamespace =
	| 'item-info'
	| 'kit-info'
	| 'reservation-info'
	| 'checkout-info'
	| 'contact-info'
	| 'ticket-info';

/**
 * Feature flags the shared Info components read. The app resolves them (e.g. Optimizely
 * `useFeature`) and passes the values in, so this package stays free of a flag SDK.
 */
export interface InfoFeatureFlags {
	hide_item_status_text?: boolean;
}

/** Components the app can swap in for the shared Info components' defaults. */
export interface InfoComponents {
	/**
	 * In-app link around `Trans` text (custody user, kit). Pass the app's router-aware link so a click
	 * navigates in place and does not bubble to a table row. Defaults to a plain `<a>`.
	 */
	Link?: ComponentType<{ href: string; children?: ReactNode }>;
}

export interface InfoConfig {
	user?: PermissionsProps['user'];
	workspace?: Pick<Group, 'profile' | 'limits' | 'itemFlags' | 'itemTypes' | 'businessHours' | 'ticketTypes'> | null;
	/**
	 * The app's translation hook, called by each Info type with its own namespace — so a namespace
	 * loads only when that Info type first renders (web lazy-loads each one as a chunk). A hook, not
	 * a `t`: this package can't call `useTranslation` itself (see `Translate`). Must keep a stable
	 * identity (module-level), as it runs as a hook inside the Info components.
	 */
	useTranslate: (ns: InfoNamespace) => Translate;
	currency?: string | null;
	locale?: string;
	formatValue?: FieldValueFormatter;
	getIcon?: FieldIconResolver;
	featureFlags?: InfoFeatureFlags;
	components?: InfoComponents;
}

const InfoConfigContext = createContext<InfoConfig | null>(null);

export const InfoProvider: FC<PropsWithChildren<InfoConfig>> = ({ children, ...config }) => (
	<InfoConfigContext.Provider value={config}>{children}</InfoConfigContext.Provider>
);

export const useInfoConfig = (): InfoConfig => {
	const config = useContext(InfoConfigContext);
	if (config == null) {
		throw new Error('An Info component was used outside of an ancestor InfoProvider');
	}
	return config;
};

/** The app's `t`, bound to one Info type's namespace. */
export const useInfoT = (ns: InfoNamespace): Translate => {
	const { useTranslate } = useInfoConfig();
	return useTranslate(ns);
};
